/**
 * Unified AttractionsMapView Component
 *
 * This component consolidates native and web versions of the AttractionsMapView.
 * It renders a map view with attractions locations, clustering, and floating action buttons (FABs)
 * for navigation and map reset. Supports detailed callouts for attractions.
 *
 * Features:
 * - Interactive map with markers and clustering.
 * - FABs for navigation and resetting the map.
 * - Detailed callouts for attractions locations.
 *
 * @module AttractionsMapView
 */

import React from "react";
import { useNavigation } from "@react-navigation/native";
import { Platform } from "react-native";
import GeneralMapView from "../../Other/MapView/GeneralMapView";
import { toTitleCase } from "../../../utils/formatting";
import AttractionsStore from "../../../storage/attractionsStore";
import { logAnalyticsEvent } from "../../../utils/firebase";

// Marker image paths
const AttractionMarkerDisabled = require("../../../assets/images/attraction-point-disabled.png");
const AttractionMarkerEnabled = require("../../../assets/images/attraction-point-enabled.png");

const AttractionsMapView = () => {
  logAnalyticsEvent("screen_view", { name: "VBGo-AttractionsMapView" });

  const navigation = useNavigation();
  const attractions = AttractionsStore((state) => state.attractions);
  const initialCenter =
    Platform.OS === "web"
      ? { lat: 36.8506, lng: -75.9779 }
      : {
          latitude: 36.74,
          longitude: -76.04,
          latitudeDelta: 0.4,
          longitudeDelta: 0.4,
        };

  const handleNavigateToListView = () => {
    navigation.navigate("AttractionsListView");
  };

  const handleNavigateToDetailView = (location) => {
    const index = attractions.findIndex((attraction) => attraction.name === location.name);
    navigation.navigate("AttractionsDetailsView", { index });
  };

  const calloutProps = {
    title: (location) => toTitleCase(location.name),
    subtitle: (location) => location.address,
  };

  return (
    <GeneralMapView
      data={attractions}
      initialCenter={initialCenter}
      markerIcons={
        Platform.OS === "android"
          ? {
              enabled: { uri: "attractionpointenabled" },
              disabled: { uri: "attractionpointdisabled" },
            }
          : {
              enabled: AttractionMarkerEnabled,
              disabled: AttractionMarkerDisabled,
            }
      }
      calloutProps={calloutProps}
      onNavToListView={handleNavigateToListView}
      onNavToDetailView={handleNavigateToDetailView}
    />
  );
};

export default AttractionsMapView;
